/**
 * CBO/Organization Management Component
 * Admin interface for managing organizations
 */

import React, { useState, useEffect } from 'react';
import { Building, Plus, Edit, Trash2, Users, CheckCircle, XCircle, Search, DollarSign, Award, Sliders } from 'lucide-react';
import { organizationsAPI, type Organization, type UserOrganization, isSuperAdmin } from '@/services/api/organizations';
import { apiService } from '@/services/api';
// OrganizationSettingsModal removed — settings now render as full page via OrganizationSettingsPage

import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { config as productConfig } from '@/src/config';
interface CBOManagementProps {
  onEditOrganization?: (org: Organization) => void;
  onCreateOrganization?: () => void;
  onNavigateToSettings?: (orgId: number, orgName: string) => void;
  onOpenClientBilling?: (orgId: number, orgName: string) => void;
  /** Jump to the User Management page for this org row. On Triveny this is
      the only way to reach user-management from the My Organization view
      (the rail doesn't surface an Admin section). */
  onNavigateToUsers?: (orgId: number, orgName: string) => void;
  refreshTrigger?: number; // Change this value to trigger a refresh
  currentUserRole?: string;
  currentUserCboId?: number;
}

export function CBOManagement({ onEditOrganization, onCreateOrganization, onNavigateToSettings, onOpenClientBilling, onNavigateToUsers, refreshTrigger, currentUserRole, currentUserCboId }: CBOManagementProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [userOrgs, setUserOrgs] = useState<UserOrganization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [selectedOrgForSettings, setSelectedOrgForSettings] = useState<Organization | null>(null);
  // Orgs whose logo URL failed to load — they fall back to the building icon
  // instead of the browser's broken-image glyph.
  const [brokenLogos, setBrokenLogos] = useState<Set<number>>(new Set());
  // On Triveny we short-circuit loadOrganizations() before calling the
  // /api/cbos endpoints, so userOrgs is always empty and isSuperAdmin()
  // returns false — hiding the "New Organization" button and edit/delete
  // actions. Triveny tenants own their own (single) tenant row, so
  // treat them as admin of that row. The actual write enforcement happens
  // backend-side via assert_actor_owns_org() in PUT /api/cbos/{id}.
  const isAdmin = productConfig.isTriveny ? true : isSuperAdmin(userOrgs);

  useEffect(() => {
    loadOrganizations();
  }, []);

  // Reload when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger !== undefined) {
      loadOrganizations();
    }
  }, [refreshTrigger]);

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      setError(null);

      // Triveny: the org listing endpoints (/api/cbos, /api/users/me/
      // organizations) live on weavein-api and 401 for platform JWTs. Triveny
      // tenants manage their own single tenant row, so resolve it AUTHORITATIVELY
      // from the platform /me (which returns the tenant's real name + cbo_id)
      // and render that one org — instead of fabricating a generic
      // "My organization" placeholder or showing nothing when activeOrgId is
      // unset. Falls back to the prop / localStorage if /me is unavailable.
      if (productConfig.isTriveny) {
        setUserOrgs([]);
        let cboId: number | null = currentUserCboId ? Number(currentUserCboId) : null;
        let orgName = 'My organization';
        try {
          const token = localStorage.getItem('auth_token') || '';
          const me = token ? await apiService.getCurrentUser(token) : null;
          const meData: any = me && me.success ? me.data : null;
          if (meData) {
            if (meData.cbo_id != null) cboId = Number(meData.cbo_id);
            if (meData.tenant_name) orgName = meData.tenant_name;
          }
        } catch {
          /* keep prop/localStorage fallback */
        }
        if (!cboId) {
          const ls = localStorage.getItem('active_cbo_id');
          if (ls) cboId = Number(ls);
        }
        if (cboId) {
          // Make sure the rest of the app has org context too.
          if (localStorage.getItem('active_cbo_id') == null) localStorage.setItem('active_cbo_id', String(cboId));
          setOrganizations([{ id: cboId, name: orgName, status: 'active', created_at: new Date().toISOString() }]);
        } else {
          setOrganizations([]);
          setError("Your workspace isn't linked to an organization yet. Contact support to finish setup.");
        }
        return;
      }

      const [orgs, myOrgs] = await Promise.all([
        organizationsAPI.listOrganizations(),
        organizationsAPI.getMyOrganizations(),
      ]);

      // Filter orgs for non-super/ops admins — they only see their own org
      const isSuperOrOps = currentUserRole === 'super_admin' || currentUserRole === 'ops_admin';
      const filteredOrgs = (isSuperOrOps || !currentUserCboId)
        ? orgs
        : orgs.filter(o => Number(o.id) === Number(currentUserCboId));
      setOrganizations(filteredOrgs);
      setUserOrgs(myOrgs);
    } catch (err: any) {
      console.error('Failed to load organizations:', err);
      setError(err.message || 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOrganization = async (orgId: number, orgName: string) => {
    if (!confirm(`Are you sure you want to delete "${orgName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await organizationsAPI.deleteOrganization(orgId);
      await loadOrganizations();
    } catch (err: any) {
      console.error('Failed to delete organization:', err);
      alert(`Failed to delete organization: ${err.message}`);
    }
  };

  const handleToggleStatus = async (org: Organization) => {
    const newStatus = org.status === 'active' ? 'inactive' : 'active';
    const confirmMessage = org.status === 'active'
      ? `Are you sure you want to deactivate "${org.name}"?`
      : `Are you sure you want to activate "${org.name}"?`;

    if (!confirm(confirmMessage)) {
      return;
    }

    try {
      await organizationsAPI.updateOrganization(org.id, { status: newStatus });
      await loadOrganizations();
    } catch (err: any) {
      console.error('Failed to update organization status:', err);
      alert(`Failed to update organization status: ${err.message}`);
    }
  };

  // Filter organizations
  const filteredOrganizations = organizations.filter(org => {
    // Search filter — name, description, or the CBO ID (TRUS-312), so admins
    // can look an organization up by its unique code.
    const matchesSearch = org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         org.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         String(org.id).includes(searchTerm.trim());

    // Status filter
    const matchesStatus = filterStatus === 'all' ||
                         (filterStatus === 'active' && org.status === 'active') ||
                         (filterStatus === 'inactive' && org.status === 'inactive');

    return matchesSearch && matchesStatus;
  });

  // Calculate stats
  const totalOrgs = organizations.length;
  const activeOrgs = organizations.filter(org => org.status === 'active').length;
  const inactiveOrgs = organizations.filter(org => org.status === 'inactive').length;

  // Customer status badge helper
  const getCustomerStatusBadge = (status: string | undefined) => {
    if (!status) return null;

    // 4-value lifecycle (post migration 286). Legacy values remain mapped so
    // any cached data still renders sensibly until the next page load.
    const statusConfig: Record<string, { label: string; color: string; icon?: string }> = {
      ACTIVE:   { label: 'Active',   color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
      TRIAL:    { label: 'Trial',    color: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200', icon: '*' },
      DEMO:     { label: 'Demo',     color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200', icon: '*' },
      INACTIVE: { label: 'Inactive', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200' },
      // Legacy
      CUSTOMER:    { label: 'Active',   color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
      OPPORTUNITY: { label: 'Demo',     color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200', icon: '*' },
      PROSPECT:    { label: 'Demo',     color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200', icon: '*' },
      CHURNED:     { label: 'Inactive', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200' },
    };

    const config = statusConfig[status] || { label: status, color: 'bg-gray-100 text-gray-800' };

    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.icon && <span className="mr-0.5 font-bold">{config.icon}</span>}
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
        <button
          onClick={loadOrganizations}
          className="mt-2 text-sm text-red-600 hover:text-red-800 font-medium"
        >
          {t('admin.try_again', 'Try Again')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-end gap-2">
        {isAdmin ? (
          <button
            onClick={onCreateOrganization}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>{t('admin.new_organization', 'New Organization')}</span>
          </button>
        ) : (
          <div style={{ padding: '8px', background: '#fef3c7', borderRadius: '8px', fontSize: '12px' }}>
            ⚠️ "New Organization" button hidden - isAdmin: {isAdmin.toString()}, userOrgs: {userOrgs.length}
          </div>
        )}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('admin.total_organizations', 'Total Organizations')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">{totalOrgs}</p>
            </div>
            <Building className="w-10 h-10 text-blue-600 dark:text-blue-400 opacity-20" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('admin.active_organizations', 'Active Organizations')}</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">{activeOrgs}</p>
            </div>
            <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400 opacity-20" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('admin.inactive_organizations', 'Inactive Organizations')}</p>
              <p className="text-2xl font-bold text-gray-400 dark:text-gray-500 mt-1">{inactiveOrgs}</p>
            </div>
            <XCircle className="w-10 h-10 text-gray-400 dark:text-gray-500 opacity-20" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0 md:space-x-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              placeholder={t('admin.search_organizations', 'Search organizations...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>

          {/* Status Filter */}
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600 dark:text-gray-400 font-medium">{t('admin.status', 'Status:')}</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as 'all' | 'active' | 'inactive')}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
            >
              <option value="all">{t('admin.all', 'All')}</option>
              <option value="active">{t('admin.active', 'Active')}</option>
              <option value="inactive">{t('admin.inactive', 'Inactive')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Organizations List */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        {filteredOrganizations.length === 0 ? (
          <div className="text-center py-12">
            <Building className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">
              {searchTerm || filterStatus !== 'all'
                ? 'No organizations match your filters'
                : 'No organizations found'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('admin.organization', 'Organization')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('admin.contact', 'Contact')}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('admin.status_1', 'Status')}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  {t('admin.customer', 'Customer')}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('admin.created', 'Created')}
                </th>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('admin.actions', 'Actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredOrganizations.map((org) => (
                <tr key={org.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center">
                      {(org.display_logo_url || org.logo_url) && !brokenLogos.has(org.id) ? (
                        <img
                          src={org.display_logo_url || org.logo_url}
                          alt={org.name}
                          className="w-8 h-8 rounded-lg object-contain bg-white"
                          onError={() => setBrokenLogos((prev) => new Set(prev).add(org.id))}
                        />
                      ) : (
                        <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                          <Building className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        </div>
                      )}
                      <div className="ml-3">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{org.name}</span>
                          {/* CBO ID appended to the org name so admins can identify
                              each organization by its unique code (TRUS-312). */}
                          <span
                            className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] text-gray-500 dark:bg-gray-700 dark:text-gray-300"
                            title="CBO ID (unique organization code)"
                          >
                            ID {org.id}
                          </span>
                          {org.is_sponsor && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-yellow-100 to-amber-100 text-amber-800 border border-amber-200">
                              <Award className="w-3 h-3" />
                            </span>
                          )}
                        </div>
                        {org.description && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 max-w-xs truncate">{org.description}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-gray-100 truncate max-w-[150px]">{org.contact_email || '—'}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{org.contact_phone || '—'}</div>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    {org.status === 'active' ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                        <CheckCircle className="w-3 h-3 mr-0.5" />
                        {t('admin.active_1', 'Active')}
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                        <XCircle className="w-3 h-3 mr-0.5" />
                        {t('admin.inactive_1', 'Inactive')}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    {getCustomerStatusBadge(org.customer_status) || (
                      <span className="text-gray-400 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                    {org.created_at ? fmt.short(org.created_at) : 'N/A'}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      {onNavigateToUsers && (
                        <button
                          onClick={() => onNavigateToUsers(org.id, org.name)}
                          className="text-emerald-600 hover:text-emerald-900 p-1 hover:bg-emerald-50 rounded transition-colors"
                          title={t('admin.manage_users', 'Manage users & invitations')}
                        >
                          <Users className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => onNavigateToSettings ? onNavigateToSettings(org.id, org.name) : setSelectedOrgForSettings(org)}
                        className="text-indigo-600 hover:text-indigo-900 p-1 hover:bg-indigo-50 rounded transition-colors"
                        title={t('admin.organization_settings', 'Organization settings')}
                      >
                        <Sliders className="w-4 h-4" />
                      </button>
                      {isAdmin && (
                        <button
                          onClick={() => onOpenClientBilling ? onOpenClientBilling(org.id, org.name) : (onNavigateToSettings ? onNavigateToSettings(org.id, org.name) : setSelectedOrgForSettings(org))}
                          className="text-green-600 hover:text-green-900 p-1 hover:bg-green-50 rounded transition-colors"
                          title={t('admin.client_billing', 'Client billing & payments')}
                        >
                          <DollarSign className="w-4 h-4" />
                        </button>
                      )}
                      {isAdmin && (
                        <button
                          onClick={() => handleToggleStatus(org)}
                          className={`p-1 rounded transition-colors ${
                            org.status === 'active'
                              ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                              : 'text-green-600 hover:text-green-900 hover:bg-green-50'
                          }`}
                          title={org.status === 'active' ? 'Deactivate organization' : 'Activate organization'}
                        >
                          {org.status === 'active' ? (
                            <XCircle className="w-4 h-4" />
                          ) : (
                            <CheckCircle className="w-4 h-4" />
                          )}
                        </button>
                      )}
                      <button
                        onClick={() => onEditOrganization?.(org)}
                        className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded transition-colors"
                        title={t('admin.edit_organization', 'Edit organization')}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      {isAdmin && (
                        <button
                          onClick={() => handleDeleteOrganization(org.id, org.name)}
                          className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded transition-colors"
                          title={t('admin.delete_organization', 'Delete organization')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* Modal removed — org settings now opens as a full page via onNavigateToSettings */}
    </div>
  );
}
